import { useCallback, useRef, useEffect } from 'react';

import { ItemProps } from '../types';

type LastSwipeProps = {
    slides: React.RefObject<Map<number, HTMLDivElement>>;
    items: ItemProps[];
    listContainerRef: React.RefObject<HTMLDivElement> | undefined;
    setLastSwipeSlideIndex: React.Dispatch<React.SetStateAction<number>>;
};

export const useFindLastSwipeSlide = function () {
    return useCallback(({ setLastSwipeSlideIndex, listContainerRef, slides, items }: LastSwipeProps) => {
        if (!slides.current || slides.current.size === 0) {
            return;
        }

        /* Тут находим элемент который является последним для переключения и записываем его в useState */
        const lastElement = slides.current.get(items.length - 1);
        const containerWidth = listContainerRef?.current?.offsetWidth;

        if (!lastElement || !containerWidth) {
            return;
        }

        const lastElementPositionAndWidthSum = lastElement.offsetLeft + lastElement.offsetWidth;
        const lastSwipeSlide = Array.from(slides.current).find(slide => {
            const slideOffset = slide[1].offsetLeft;
            const slideOffsetAndContainerWidth = slideOffset + containerWidth;

            return slideOffsetAndContainerWidth >= lastElementPositionAndWidthSum;
        });

        if (lastSwipeSlide) {
            setLastSwipeSlideIndex(lastSwipeSlide[0]);
        }
    }, []);
};

type FindActualSlideProps = {
    slides: React.RefObject<Map<number, HTMLDivElement>>;
    currentSlideIndex: number;
    setCurrentSlideIndex: (slideIndex: number) => void;
};

export const useFindActualSlideIndex = function (initialSlides: Map<number, HTMLDivElement>) {
    const memoizedSlides = useRef<Map<number, HTMLDivElement>>(new Map());

    useEffect(
        () => {
            memoizedSlides.current = new Map(initialSlides);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- требуется только при маунте
        [],
    );

    return useCallback(
        ({ slides, currentSlideIndex, setCurrentSlideIndex }: FindActualSlideProps) => {
            if (memoizedSlides && slides.current) {
                // Массив ключей слайдов
                const slidesIds = Array.from(slides.current.keys());

                // Получаем индекс текущего слайда
                const index = Array.from(memoizedSlides.current.keys()).indexOf(currentSlideIndex);

                // Устанавливаем актуальный слайд по данному индексу или последний, если такого слайда нет
                setCurrentSlideIndex(slidesIds[index] ?? slidesIds[slidesIds.length - 1] ?? 0);
            }

            memoizedSlides.current = new Map(slides.current);
        },
        [memoizedSlides],
    );
};
